import React, {useState, useCallback, useRef, useEffect} from 'react';
import {MouseEvent} from 'react';
import {Grid, Typography} from '@mui/material';
import {FileRejection, useDropzone} from 'react-dropzone';
import AurButton from '../aur-button/AurButton';
import {useNavigate} from 'react-router-dom';
import * as XLSX from 'xlsx';
import {t} from 'i18next';
import {
  validateHeaders,
  expectedHeaders,
  IUploadFileData,
  ensureFields,
  isRowEmpty,
} from '../../../utils/bulkUserUploadUtlis';
import {useDispatch, useSelector} from 'react-redux';
import {
  setBulkUserFile,
  setBulkUserFileName,
  setDuplicateEmail,
  setFileProcessError,
} from '../../../redux/slices/bulkUserFileNameSlice';
import {bulkUploadDownloadTemplate} from '../../../api/UserManagementService';
import AurBanner from '../aur-banner/AurBanner';
import {RootState} from '../../../redux/store';
import {UrlPathEnums} from '../../../utils/enumUtils';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';

interface IUploadBox {
  title?: string;
  description?: string;
  note?: string;
  FileUploadIcon?: React.ElementType;
  buttonLabel?: string;
  supportText?: string;
  dragAndDropText?: string;
  buttonIcon?: React.ReactNode;
  guideText?: string;
  isDragDrop?: boolean;
  borderClass?: string;
  errorStateBorderClass?: string;
  handleFile?: (files: File[]) => void;
  buttonHint?: string;
  ButtonClassNames?: string;
  dataTestId?: string;
  orText?: boolean;
  descriptionClass?: string;
}

/**
 * Define the aur-uploadBox component
 * all props content has been set to optional to add it into upload box.
 * prop isDragDrop = true the container will act as drag or drop .
 */
const AurUploadBox = ({
  handleFile,
  title = '',
  description = '',
  note = '',
  FileUploadIcon,
  buttonLabel = '',
  dragAndDropText = '',
  buttonIcon,
  guideText = '',
  borderClass,
  errorStateBorderClass,
  buttonHint,
  ButtonClassNames,
  orText,
  dataTestId,
  isDragDrop = true,
  descriptionClass = '',
}: IUploadBox) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [successState, setSuccessState] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const [generalState, setGeneralState] = useState(true);
  const [validationError, setValidationError] = useState<string>('');
  const [lastIndexFile, setlastIndexFile] = useState<number>(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const bulkUserFileError: boolean = useSelector(
    (state: RootState) => state.bulkUserFile.fileProcessError
  );
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).bulkUploadClientId;

  const selectedClientName = useSelector(
    (state: RootState) => state.bulkUserFile.selectedClient
  );
  useEffect(() => {
    if (bulkUserFileError) {
      setValidationError(
        `${t('common.aurFileUploadErrorState.duplicateEmailError')}`
      );
      setErrorState(true);
      setSuccessState(false);
      setGeneralState(true);
    }
  }, [bulkUserFileError]);

  useEffect(() => {
    if (validationError != '' && isDragDrop) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.ERROR,
        page: GA_DATA_MODE_AUTO,
        user: GA_DATA_MODE_AUTO,
        error: {
          text: validationError,
          type: 'validation',
        },
      });
    }
  }, [validationError, isDragDrop]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      dispatch(setFileProcessError(false));
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dispatch]);

  const handleFileValidation = (acceptedFiles: File[]) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const file = acceptedFiles[0];

    if (file) {
      const lastDotIndex = file.name.lastIndexOf('.');
      if (lastDotIndex != -1) setlastIndexFile(lastDotIndex);
      setFileName(file.name);
      dispatch(setBulkUserFileName(file.name ? file.name : ''));
      dispatch(setBulkUserFile(file));

      if (allowedTypes.includes(file.type)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const binaryStr = e.target?.result;
            const workbook = XLSX.read(binaryStr, {type: 'binary'});
            setErrorState(false);
            setSuccessState(true);
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const headersJson = XLSX.utils.sheet_to_json<string[]>(worksheet, {
              header: 1,
            });
            const headersRow = headersJson[0] || [];
            const filteredheader = headersRow.filter(
              (item: string | number) => item !== 0
            );
            if (validateHeaders(filteredheader, expectedHeaders)) {
              const jsonData = XLSX.utils.sheet_to_json<IUploadFileData>(
                worksheet,
                {header: filteredheader, range: 1}
              );
              const formatData = ensureFields(jsonData);
              const filteredDataRows = formatData.filter(
                (row) => !isRowEmpty(row, filteredheader)
              );

              if (filteredDataRows.length === 0) {
                setSelectedFile(null);
                setSuccessState(false);
                setGeneralState(true);
                setErrorState(true);

                setValidationError(
                  `${t('common.aurFileUploadErrorState.emptyError')}`
                );
              } else if (filteredDataRows.length > 250) {
                setSelectedFile(null);
                setSuccessState(false);
                setGeneralState(true);
                setErrorState(true);
                setValidationError(
                  `${t('common.aurFileUploadErrorState.moreUserError')}`
                );
              } else {
                setSuccessState(true);
                setGeneralState(false);
                setSelectedFile(file);
                setErrorState(false);
                handleFile?.(acceptedFiles); // Pass the file up to the parent component
                setValidationError('');
              }
            } else {
              setValidationError(
                `${t('common.aurFileUploadErrorState.headersError')}`
              );
              setSelectedFile(null);
              setSuccessState(false);
              setGeneralState(true);
              setErrorState(true);
            }
          } catch (error) {
            setValidationError(
              file.name +
                ' ' +
                `${t('common.aurFileUploadErrorState.uploadErrorDescription')}`
            );
            setErrorState(true);
            setSuccessState(false);
            setValidationError('');
            setGeneralState(true);
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        setErrorState(true);
        setSuccessState(false);
        setValidationError('');
        setGeneralState(true);
      }
    }
  };

  /**
   * Define the handleButtonClick function
   * if the button label is Download Template it will download the sample template
   * or else it will trigger the file select.
   */
  // Call the API to download standard template
  const handleButtonClick = () => {
    // Assuming the response is a Blob representing the file
    const handleDownload = async () => {
      try {
        const response = await bulkUploadDownloadTemplate(clientId);
        // Create a Blob from the response data and Set the default file name
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${selectedClientName}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        trackGAEvent({
          eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
          file_extension: GA_FILE_EXTENTIONS.XLSX,
          click: {
            name: t('gaTracking.users.downloadTemplate'),
            type: GA_CLICK_TYPES.BUTTON,
            region: GA_REGIONS.BODY,
            action: t('gaTracking.users.downloadTemplateAction'),
          },
        });
      } catch (error) {
        console.error('Error downloading the template:', error);
      }
    };
    if (buttonLabel === 'Download Template') {
      handleDownload();
    } else {
      // Trigger file input click for upload
      fileInputRef.current?.click();
    }
  };

  const handleNavigateClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(setFileProcessError(false));
    navigate(UrlPathEnums.UsersBulkUploadFileprocessing);
    dispatch(setDuplicateEmail(false));
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.users.uploadFile'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.users.uploadFileAction'),
      },
    });
  };

  const onDragDropFileHandler = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      // Handle accepted files
      if (acceptedFiles.length > 0) {
        handleFileValidation(acceptedFiles);
      }

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        setSuccessState(false);
        setErrorState(true);

        // Log rejected file names
        rejectedFiles.forEach((rejection) => {
          setValidationError(
            rejection.file.name +
              ' ' +
              `${t('common.aurFileUploadErrorState.uploadErrorDescription')}`
          );
        });
      }

      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.users.dragAndDrop'),
          type: GA_CLICK_TYPES.DRAG_DROP,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.users.selectedFileAction'),
        },
      });
    },
    []
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive: dragActive,
  } = useDropzone({
    onDrop: onDragDropFileHandler,
    multiple: false,
    noClick: true,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
    },
    disabled: !isDragDrop,
  });

  return (
    <Grid
      container
      className={`upload-commonContainer upload-${errorState ? errorStateBorderClass : borderClass} drag-${dragActive}`}
      {...(isDragDrop ? getRootProps() : {})}
    >
      <Grid
        item
        xs={12}
        md={6}
        className={`upload-innerContainer ${errorState ? 'error-innerContainer' : 'noerror-innerContainer'}`}
      >
        <Typography className="typo-text headline3_size mist-color warning-icon-title">
          {title}
        </Typography>
        <div className="upload-innerContent">
          <Typography
            className={`body2_size mist-color-500 ${descriptionClass}`}
            component="span"
          >
            {description}
          </Typography>
          {note && (
            <Typography
              className={`body2_size mist-color-500 ${errorState ? 'error-note' : ''}`}
            >
              <span className="body2_size mist-color"> {t('common.note')}</span>
              {note}
            </Typography>
          )}
          {guideText && (
            <Typography className="body2_size lightBlue-300 guide-text">
              {guideText}
            </Typography>
          )}

          {validationError != '' && isDragDrop && (
            <AurBanner
              errorText={validationError}
              showIcon={true}
              textContainerCustomClass="textContainerCustomClass"
              errorType="error"
            />
          )}
        </div>
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        className={
          successState
            ? 'upload-imageContainer-success-state'
            : 'upload-imageContainer'
        }
      >
        {successState && fileName && selectedFile && (
          <>
            <Typography className="typo-text body2_size upload-text">
              {t('common.aurUploadBox.uploadFileText')}
            </Typography>
            <Typography className="file-details typo-text headline3_size ">
              {`${fileName.slice(0, lastIndexFile)}.${fileName.slice(lastIndexFile + 1).toUpperCase()}`}
            </Typography>
          </>
        )}

        {!successState && FileUploadIcon && <FileUploadIcon />}
        {!successState && dragAndDropText && (
          <Typography className="typo-text headline3_size drag-text">
            {dragAndDropText}
          </Typography>
        )}

        {orText && !successState && (errorState || generalState) ? (
          <Typography className="body3_size mist-color-900 or-text">
            {t('overlay.orText')}
          </Typography>
        ) : null}

        <input
          {...getInputProps()}
          ref={fileInputRef}
          style={{display: 'none'}}
          data-testid="file-input"
          onChange={(e) => {
            if (e.target.files) {
              e.target.blur();
              handleFileValidation(Array.from(e.target.files));
            }
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: successState
                  ? `${t('gaTracking.users.chooseDifferentFile')}`
                  : buttonLabel,
                type: GA_CLICK_TYPES.BUTTON,
                region: GA_REGIONS.BODY,
                action: t('gaTracking.users.selectedFileAction'),
              },
            });
          }}
          accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        />

        {successState ? (
          <div className="upload-file-upload-button">
            <AurButton
              label={t('common.aurUploadBox.uploadFile')}
              type="continue"
              onClick={(e) => handleNavigateClick(e)}
              startIcon={buttonIcon}
              classNames={`upload-btn typo-text buttonLarge_size ${ButtonClassNames}`}
              ariaLabel={buttonHint}
              dataTestId={dataTestId}
              tabIndex={0}
            />
          </div>
        ) : null}

        <AurButton
          label={
            successState
              ? `${t('common.aurUploadBox.bulkUserUploadChooseFileText')}`
              : buttonLabel
          }
          type="continue"
          onClick={handleButtonClick}
          startIcon={buttonIcon}
          classNames={`upload-btn typo-text buttonLarge_size ${successState ? `aur-successState-button` : ButtonClassNames}`}
          ariaLabel={buttonHint}
          dataTestId={dataTestId}
          tabIndex={1}
          autoFocus={false}
        />
      </Grid>
    </Grid>
  );
};

export default AurUploadBox;
